/**
 * Converts an RGB array to hexadecimal color code.
 * @param {[number, number, number]} rgb - The RGB array.
 * @returns {string} - The hexadecimal color code.
 */
export const rgbToHex = ([r, g, b]: [number, number, number]): string => {
  return "#" + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)
}

/**
 * Converts a hexadecimal color code to an RGB array.
 * @param {string} hex - The hexadecimal color code.
 * @returns {string} - The RGB string 'r, g, b'.
 */
export const hexToRgbString = (hex: string): string => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, "")

  // Parse the r, g, b values
  const bigint = parseInt(hex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return `${(r / 255).toFixed(3)}, ${(g / 255).toFixed(3)}, ${(b / 255).toFixed(3)}`
}

/**
 * Converts a tint RGB string to hexadecimal color code.
 * @param {string} tintRgb - The tint RGB string in "0.000 0.000 0.000" format.
 * @returns {string} - The hexadecimal color code.
 */
export const tintRgbTohex = (tintRgb: string): string => {
  const parsedColor = tintRgb.split(" ").map((color: string) => Math.round(parseFloat(color) * 255))
  return rgbToHex(parsedColor as [number, number, number])
}

/**
 * Converts a color from fractional RGB format to hexadecimal.
 * @param {string} colorString - The color string in "0.043, 0.220, 0.765" format.
 * @returns {string} - The hexadecimal color code.
 */
export const fractionalRgbToHex = (colorString: string): string => {
  if (colorString.includes(",")) colorString = colorString.replaceAll(",", "")

  const fractionalValues = colorString.split(" ").map(parseFloat)

  const rgbValues = fractionalValues.map((value) => Math.round(value * 255))

  const hexValues = rgbValues.map((value) => {
    const hex = value.toString(16)
    return hex.length === 1 ? "0" + hex : hex
  })

  const result = hexValues.join("")
  return "#" + result
}

/**
 * Converts a hexadecimal or rbg color code to an RGB array.
 * @returns {string} - The RGB string 'r, g, b'.
 * @param color
 */
export const hexOrRgbToRgbString = (color: string): string => {
  if (color)
    if (color.at(0) === "#") return hexToRgbString(color)
    else if (color.includes(",")) return color.replace(/,/g, "")

  return color
}
